import { useCallback, useMemo, useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { DialogProps } from '@mui/material/Dialog';
import { PaperProps } from '@mui/material/Paper';
import {
  BaseDialogProps,
  BaseDialogController,
  BaseDialogStyles,
  BaseDialogState,
  BaseDialogActions,
  BaseDialogComputed,
  DialogCloseReason,
  DEFAULT_BASE_DIALOG_CONFIG,
  isModalMode,
  isEmbeddedMode,
  isFullscreenVariant,
  isCompactVariant,
} from './BaseDialog.model';
import { useChangeDetection, UseChangeDetectionOptions } from '../../hooks/useChangeDetection';

/**
 * Hook for enhanced state management with advanced change detection
 * Provides complete dialog state, actions, and computed properties
 */
export const useBaseDialogState = <TData = any>(
  initialData?: TData,
  validator?: (data: TData) => boolean,
  changeDetectionOptions?: UseChangeDetectionOptions
): {
  state: BaseDialogState<TData>;
  actions: BaseDialogActions<TData>;
  computed: BaseDialogComputed;
  changeDetection: ReturnType<typeof useChangeDetection<TData>>;
} => {
  // Internal state
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TData | null>(initialData || null);

  // Advanced change detection
  const changeDetection = useChangeDetection<TData>({
    ignoreFields: ['updatedAt', 'createdAt', '__typename', 'lastModified'],
    debug: process.env.NODE_ENV === 'development',
    validator,
    ...changeDetectionOptions
  });

  // Actions with change detection integration
  const actions: BaseDialogActions<TData> = useMemo(() => ({
    open: () => {
      setIsOpen(true);
      setError(null);
      // Start change tracking when dialog opens
      if (data) {
        changeDetection.startTracking(data);
      }
    },
    close: () => {
      setIsOpen(false);
      setError(null);
      // Reset change tracking when dialog closes
      changeDetection.reset();
    },
    toggle: () => {
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);
      setError(null);

      if (newIsOpen && data) {
        changeDetection.startTracking(data);
      } else if (!newIsOpen) {
        changeDetection.reset();
      }
    },
    setData: (newData: TData) => {
      setData(newData);
      setError(null);
      // Update current data in change detection
      if (isOpen) {
        changeDetection.updateCurrent(newData);
      }
    },
    clearData: () => {
      setData(null);
      setError(null);
      changeDetection.reset();
    },
    setLoading: (loading: boolean) => {
      setIsLoading(loading);
    },
    setError: (errorMessage: string | null) => {
      setError(errorMessage);
      setIsLoading(false);
    },
  }), [isOpen, data, changeDetection]);

  // Enhanced computed properties using advanced change detection
  const computed: BaseDialogComputed = useMemo(() => {
    // Use advanced change detection instead of simple JSON comparison
    const hasChanges = changeDetection.isDirty();

    const isValid = changeDetection.isValid();

    const canClose = !isLoading && (!hasChanges || isValid);

    return {
      hasChanges,
      isValid,
      canClose,
      // Additional computed properties from change detection
      changes: changeDetection.getChanges(),
      stats: changeDetection.getStats()
    };
  }, [isLoading, changeDetection]);

  // State object
  const state: BaseDialogState<TData> = {
    isOpen,
    isLoading,
    error,
    data,
  };

  // Update change tracking when data changes
  useEffect(() => {
    if (isOpen && data) {
      // If dialog is already open and we get new data, start tracking it
      changeDetection.startTracking(data);
    }
  }, [isOpen, data, changeDetection]);

  return { state, actions, computed, changeDetection };
};

/**
 * BaseDialog controller hook
 * Handles dialog logic, theme integration, and style computation
 * Does NOT manage open/close state - that's parent's responsibility
 */
export const useBaseDialogController = <TData = any>(
  props: BaseDialogProps<TData>
): BaseDialogController<TData> => {
  const theme = useTheme();

  const {
    mode = DEFAULT_BASE_DIALOG_CONFIG.defaultMode,
    variant = DEFAULT_BASE_DIALOG_CONFIG.defaultVariant,
    onClose,
    onOpen,
    onTransitionEnd,
    onTransitionExited,
    onDataChange,
    disableBackdropClick = DEFAULT_BASE_DIALOG_CONFIG.defaultDisableBackdropClick,
    disableEscapeKeyDown = DEFAULT_BASE_DIALOG_CONFIG.defaultDisableEscapeKeyDown,
    disableAutoFocus = DEFAULT_BASE_DIALOG_CONFIG.defaultDisableAutoFocus,
    disableRestoreFocus = DEFAULT_BASE_DIALOG_CONFIG.defaultDisableRestoreFocus,
    disableEnforceFocus = DEFAULT_BASE_DIALOG_CONFIG.defaultDisableEnforceFocus,
    maxWidth = DEFAULT_BASE_DIALOG_CONFIG.defaultMaxWidth,
    fullScreen = false,
    fullWidth = false,
    keepMounted = DEFAULT_BASE_DIALOG_CONFIG.defaultKeepMounted,
    zIndex = DEFAULT_BASE_DIALOG_CONFIG.defaultZIndex,
    TransitionComponent,
    transitionDuration,
    TransitionProps,
    sx,
    className,
    data,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
  } = props;

  // Compute theme-aware styles using ONLY theme tokens
  const styles: BaseDialogStyles = useMemo(() => {
    // Variant-specific styling
    const variantStyles = (() => {
      if (isFullscreenVariant(variant) || fullScreen) {
        return {
          margin: 0,
          maxWidth: '100vw',
          maxHeight: '100vh',
          borderRadius: 0,
          width: '100vw',
          height: '100vh',
          overflow: 'hidden',
        };
      }

      if (isCompactVariant(variant)) {
        return {
          padding: theme.spacing(1),
          maxWidth: theme.breakpoints.values.sm,
        };
      }

      // Default variant
      return {
        // Padding уже задан глобально в ThemeModeContext (24px), не дублируем
        maxHeight: `calc(100vh - ${theme.spacing(8)})`, // Leave space for backdrop
      };
    })();

    return {
      paper: {
        backgroundColor: theme.palette.background.paper, // NOT '#ffffff'
        color: theme.palette.text.primary,               // NOT '#000000'
        borderRadius: theme.shape.borderRadius,          // NOT '8px'
        boxShadow: theme.shadows[24],                    // NOT hardcoded shadow
        border: `1px solid ${theme.palette.divider}`,   // NOT hardcoded border
        zIndex: zIndex,                                  // Custom z-index
        // Variant-specific styles
        ...variantStyles,
        // Smooth transitions
        transition: theme.transitions.create(
          ['background-color', 'box-shadow', 'border-color', 'transform'],
          {
            duration: typeof transitionDuration === 'number'
              ? transitionDuration
              : theme.transitions.duration.short,
          }
        ),
        ...sx, // Allow custom overrides
      },
      backdrop: {
        backgroundColor: theme.palette.action.disabled, // NOT hardcoded backdrop
        backdropFilter: 'blur(2px)',                    // Modern backdrop effect
        transition: theme.transitions.create('opacity', {
          duration: theme.transitions.duration.short,
        }),
      },
      container: {
        padding: theme.spacing(2), // Consistent spacing
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(1), // Tighter on mobile
        },
      },
    };
  }, [theme, variant, fullScreen, zIndex, transitionDuration, sx]);

  // Enhanced onClose handler with reason filtering
  const handleClose = useCallback(
    (event: {}, reason: DialogCloseReason) => {
      // Filter out backdrop clicks if disabled
      if (reason === 'backdropClick' && disableBackdropClick) {
        return;
      }

      // Filter out escape key if disabled
      if (reason === 'escapeKeyDown' && disableEscapeKeyDown) {
        return;
      }

      // Call parent's onClose handler
      onClose?.(event, reason);
    },
    [onClose, disableBackdropClick, disableEscapeKeyDown]
  );

  // Prepare props for MUI Dialog component (modal mode)
  const dialogProps: Partial<DialogProps> = useMemo(() => {
    // Use fullWidth only when explicitly set to true
    const computedFullWidth = fullWidth === true;

    return {
      maxWidth: maxWidth,
      fullWidth: computedFullWidth,
      fullScreen: isFullscreenVariant(variant) || fullScreen,
      keepMounted,
      disableEscapeKeyDown,
      disableAutoFocus,
      disableRestoreFocus,
      disableEnforceFocus,
      className,
      'data-testid': dataTestId,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      // Animation props
      TransitionComponent,
      transitionDuration,
      TransitionProps: {
        ...TransitionProps,
        onEntered: () => {
          onOpen?.();
          TransitionProps?.onEntered?.();
        },
        onExited: () => {
          onTransitionExited?.();
          TransitionProps?.onExited?.();
        },
        onTransitionEnd: () => {
          onTransitionEnd?.();
          TransitionProps?.onTransitionEnd?.();
        },
      },
      // Modern slotProps API for paper
      slotProps: {
        paper: {
          sx: styles.paper,
          ...props.slotProps?.paper,
        },
      },
      // Backdrop styling via BackdropProps (MUI compatibility)
      BackdropProps: {
        sx: {
          ...styles.backdrop,
          zIndex: zIndex - 1, // Backdrop should be behind dialog
          ...(props.slotProps?.backdrop?.sx as any),
        },
      },
      // Modern onClose handler
      onClose: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
        handleClose(event, reason);
      },
    };
  }, [
    maxWidth,
    fullWidth,
    fullScreen,
    variant,
    keepMounted,
    disableEscapeKeyDown,
    disableAutoFocus,
    disableRestoreFocus,
    disableEnforceFocus,
    className,
    dataTestId,
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy,
    TransitionComponent,
    transitionDuration,
    TransitionProps,
    onOpen,
    onTransitionEnd,
    onTransitionExited,
    zIndex,
    styles.backdrop,
    styles.paper,
    props.slotProps,
    handleClose,
  ]);

  // Prepare props for Paper component (embedded mode)
  const paperProps: PaperProps = useMemo(() => {
    return {
      elevation: 0, // No shadow for embedded mode
      variant: 'outlined', // Use border instead of shadow
      className,
      'data-testid': dataTestId,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      sx: {
        // Base embedded mode styles
        maxHeight: 'none', // No height restriction
        boxShadow: 'none', // No shadow
        // Apply theme styles (includes custom sx overrides)
        ...styles.paper,
        // Only apply default border if no custom border is set in sx
        ...(!(sx as any)?.border && { border: `1px solid ${theme.palette.divider}` }),
      },
      ...props.slotProps?.paper,
    };
  }, [
    className,
    dataTestId,
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy,
    styles.paper,
    theme.palette.divider,
    props.slotProps?.paper,
  ]);

  // Mode checking
  const isModal = isModalMode(mode);
  const isEmbedded = isEmbeddedMode(mode);

  // Optional enhanced state management
  const { state, actions, computed } = useBaseDialogState(data, props.onDataChange ? undefined : undefined);

  // Data change effect
  useEffect(() => {
    if (data && onDataChange) {
      onDataChange(data);
    }
  }, [data, onDataChange]);

  return {
    styles,
    handleClose,
    dialogProps,
    paperProps,
    isModal,
    isEmbedded,
    state,
    actions,
    computed,
  };
};

/**
 * Utility function to create responsive dialog props
 * Automatically sets fullScreen on mobile devices
 */
export const useResponsiveDialogProps = (
  baseProps: BaseDialogProps
): BaseDialogProps => {
  const theme = useTheme();

  // Use theme breakpoints to determine mobile
  const isMobile = theme.breakpoints.values.sm > window.innerWidth;

  return {
    ...baseProps,
    fullScreen: baseProps.fullScreen || isMobile,
    maxWidth: isMobile ? false : baseProps.maxWidth,
  };
};

/**
 * Utility to merge dialog styles with custom styles
 * Ensures theme tokens are preserved
 */
export const mergeDialogStyles = (
  baseStyles: BaseDialogStyles,
  customStyles?: Partial<BaseDialogStyles>
): BaseDialogStyles => {
  if (!customStyles) {
    return baseStyles;
  }

  return {
    paper: customStyles.paper ? { ...baseStyles.paper, ...customStyles.paper } : baseStyles.paper,
    backdrop: customStyles.backdrop ? { ...baseStyles.backdrop, ...customStyles.backdrop } : baseStyles.backdrop,
    container: customStyles.container ? { ...baseStyles.container, ...customStyles.container } : baseStyles.container,
  } as BaseDialogStyles;
};